import { Button, Tooltip } from 'antd'
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent
} from 'react'
import { useTranslation } from 'react-i18next'

import { OrgChartNodeCard } from './OrgChartNode'
import type { OrgChartNode } from './types'

// ----- Layout types & constants -----

type LayoutNode = {
  node: OrgChartNode
  x: number
  y: number
  width: number
  height: number
  children: LayoutNode[]
}

const NODE_WIDTHS: Record<string, number> = {
  company: 220,
  division: 200,
  department: 180,
  team: 160
}
const NODE_HEIGHT = 84
const H_GAP = 24
const V_GAP = 60

// ----- Tree layout algorithm -----

function computeSubtreeWidth(
  node: OrgChartNode,
  collapsedIds: Set<string>
): number {
  const ownWidth = NODE_WIDTHS[node.type] ?? 180
  const isCollapsed = collapsedIds.has(node.id)

  if (node.children.length === 0 || isCollapsed) {
    return ownWidth
  }

  const childrenWidth = node.children.reduce((sum, child, i) => {
    const w = computeSubtreeWidth(child, collapsedIds)

    return sum + w + (i > 0 ? H_GAP : 0)
  }, 0)

  return Math.max(ownWidth, childrenWidth)
}

function positionNodes(
  node: OrgChartNode,
  collapsedIds: Set<string>,
  startX: number,
  startY: number
): LayoutNode {
  const ownWidth = NODE_WIDTHS[node.type] ?? 180
  const subtreeW = computeSubtreeWidth(node, collapsedIds)
  const isCollapsed = collapsedIds.has(node.id)
  const visibleChildren =
    isCollapsed || node.children.length === 0 ? [] : node.children

  // Center self within subtree width
  const x = startX + subtreeW / 2 - ownWidth / 2

  if (visibleChildren.length === 0) {
    return {
      node,
      x,
      y: startY,
      width: ownWidth,
      height: NODE_HEIGHT,
      children: []
    }
  }

  // Position children
  const childLayouts: LayoutNode[] = []
  let childX = startX
  for (const child of visibleChildren) {
    const childSubtreeW = computeSubtreeWidth(child, collapsedIds)
    childLayouts.push(
      positionNodes(child, collapsedIds, childX, startY + NODE_HEIGHT + V_GAP)
    )
    childX += childSubtreeW + H_GAP
  }

  return {
    node,
    x,
    y: startY,
    width: ownWidth,
    height: NODE_HEIGHT,
    children: childLayouts
  }
}

// ----- Flatten helpers -----

function flattenNodes(layout: LayoutNode): LayoutNode[] {
  const result: LayoutNode[] = [layout]
  for (const child of layout.children) {
    result.push(...flattenNodes(child))
  }
  return result
}

type ConnectorPath = { key: string; d: string }

function collectConnectors(layout: LayoutNode): ConnectorPath[] {
  const paths: ConnectorPath[] = []
  for (const child of layout.children) {
    const startX = layout.x + layout.width / 2
    const startY = layout.y + layout.height
    const endX = child.x + child.width / 2
    const endY = child.y
    const midY = (startY + endY) / 2
    // Orthogonal "comb" routing: vertical stem down to a shared bus at midY,
    // horizontal along the bus, then a vertical drop to the child. Collinear
    // segments from sibling edges overlap exactly, so the result reads as one
    // clean stem + bus instead of a pile of overlapping curves.
    paths.push({
      key: `${layout.node.id}-${child.node.id}`,
      d: `M ${startX} ${startY} V ${midY} H ${endX} V ${endY}`
    })
    paths.push(...collectConnectors(child))
  }
  return paths
}

// ----- Component -----

type Props = {
  tree: OrgChartNode
  selectedNodeId: string | null
  collapsedIds: Set<string>
  onSelectNode: (id: string) => void
  onToggleCollapse: (id: string) => void
  bordered?: boolean
}

export const OrgChartTree = ({
  tree,
  selectedNodeId,
  collapsedIds,
  onSelectNode,
  onToggleCollapse,
  bordered = true
}: Props) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 })
  const [isDragging, setIsDragging] = useState(false)
  const dragStart = useRef({ x: 0, y: 0 })
  const pointerInsideRef = useRef(false)

  // Layout
  const layout = useMemo(
    () => positionNodes(tree, collapsedIds, 0, 0),
    [tree, collapsedIds]
  )

  const allNodes = useMemo(() => flattenNodes(layout), [layout])
  const allConnectors = useMemo(() => collectConnectors(layout), [layout])

  // Compute SVG viewable bounds with padding
  const bounds = useMemo(() => {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const n of allNodes) {
      minX = Math.min(minX, n.x)
      minY = Math.min(minY, n.y)
      maxX = Math.max(maxX, n.x + n.width)
      maxY = Math.max(maxY, n.y + n.height)
    }
    const padding = 40

    return {
      x: minX - padding,
      y: minY - padding,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2
    }
  }, [allNodes])

  // Center the tree in the container on mount and when layout changes
  const centerTree = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const containerW = el.clientWidth
    const containerH = el.clientHeight
    const treeW = bounds.width
    const treeH = bounds.height
    const fitScale = Math.min(containerW / treeW, containerH / treeH, 1)
    const scaledW = treeW * fitScale
    const scaledH = treeH * fitScale
    setTransform({
      x: (containerW - scaledW) / 2 - bounds.x * fitScale,
      y: (containerH - scaledH) / 2 - bounds.y * fitScale,
      scale: fitScale
    })
  }, [bounds])

  useEffect(() => {
    centerTree()
  }, [centerTree])

  // Handlers
  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      setIsDragging(true)
      dragStart.current = {
        x: e.clientX - transform.x,
        y: e.clientY - transform.y
      }
    },
    [transform.x, transform.y]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!isDragging) return
      setTransform((prev) => ({
        ...prev,
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y
      }))
    },
    [isDragging]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false)
    pointerInsideRef.current = false
  }, [])

  const handleMouseEnter = useCallback(() => {
    pointerInsideRef.current = true
  }, [])

  const handleDoubleClick = useCallback(() => {
    centerTree()
  }, [centerTree])

  const handleNodeClick = useCallback(
    (nodeId: string) => {
      onSelectNode(nodeId)
    },
    [onSelectNode]
  )

  // Scale around a fixed screen point (keeps the world point under the cursor /
  // the chosen origin pinned while zooming).
  const zoomAtPoint = useCallback(
    (factor: number, originX: number, originY: number) => {
      setTransform((prev) => {
        const nextScale = Math.min(2, Math.max(0.3, prev.scale * factor))
        if (nextScale === prev.scale) return prev
        const worldX = (originX - prev.x) / prev.scale
        const worldY = (originY - prev.y) / prev.scale

        return {
          scale: nextScale,
          x: originX - worldX * nextScale,
          y: originY - worldY * nextScale
        }
      })
    },
    []
  )

  const zoomAtCenter = useCallback(
    (factor: number) => {
      const el = containerRef.current
      if (!el) return
      zoomAtPoint(factor, el.clientWidth / 2, el.clientHeight / 2)
    },
    [zoomAtPoint]
  )

  const handleZoomIn = useCallback(() => zoomAtCenter(1.2), [zoomAtCenter])
  const handleZoomOut = useCallback(() => zoomAtCenter(1 / 1.2), [zoomAtCenter])

  // Ctrl/Cmd + wheel (and trackpad pinch, which reports ctrlKey) zooms toward
  // the cursor. Plain wheel is left untouched so the page scrolls normally.
  // Native non-passive listener so we can preventDefault the browser page zoom.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      zoomAtPoint(
        e.deltaY < 0 ? 1.1 : 1 / 1.1,
        e.clientX - rect.left,
        e.clientY - rect.top
      )
    }
    el.addEventListener('wheel', onWheel, { passive: false })

    return () => el.removeEventListener('wheel', onWheel)
  }, [zoomAtPoint])

  // Cmd/Ctrl + (+ / - / 0) zoom in / out / fit while the pointer is over the
  // chart. Scoped to hover so it doesn't hijack the browser's own zoom keys.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!pointerInsideRef.current || !(e.ctrlKey || e.metaKey)) return
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        zoomAtCenter(1.2)
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        zoomAtCenter(1 / 1.2)
      } else if (e.key === '0') {
        e.preventDefault()
        centerTree()
      }
    }
    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [zoomAtCenter, centerTree])

  const handleResetView = useCallback(() => {
    centerTree()
  }, [centerTree])

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: 600,
        overflow: 'hidden',
        border: bordered
          ? '1px solid var(--ant-color-border-secondary)'
          : 'none',
        borderRadius: bordered ? 8 : 0,
        background: 'var(--ant-color-bg-container)',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={handleDoubleClick}
    >
      {/* Zoom controls */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 4,
          zIndex: 10
        }}
      >
        <Tooltip title={t('workspace.orgChart.zoomIn')}>
          <Button
            size="small"
            icon={<ZoomIn size={14} />}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleZoomIn}
          />
        </Tooltip>
        <Tooltip title={t('workspace.orgChart.zoomOut')}>
          <Button
            size="small"
            icon={<ZoomOut size={14} />}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleZoomOut}
          />
        </Tooltip>
        <Tooltip title={t('workspace.orgChart.resetView')}>
          <Button
            size="small"
            icon={<Maximize2 size={14} />}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleResetView}
          />
        </Tooltip>
      </div>

      <svg width="100%" height="100%" style={{ display: 'block' }}>
        <g
          transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}
        >
          {/* Connecting lines */}
          {allConnectors.map((conn) => (
            <path
              key={conn.key}
              d={conn.d}
              fill="none"
              stroke="var(--ant-color-border)"
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {/* Nodes */}
          {allNodes.map((layoutNode) => {
            const hasVisibleChildren =
              layoutNode.node.children.length > 0 &&
              layoutNode.node.type !== 'team'

            return (
              <foreignObject
                key={layoutNode.node.id}
                x={layoutNode.x}
                y={layoutNode.y}
                width={layoutNode.width}
                height={layoutNode.height + (hasVisibleChildren ? 12 : 0)}
                style={{ overflow: 'visible' }}
              >
                <OrgChartNodeCard
                  node={layoutNode.node}
                  isSelected={selectedNodeId === layoutNode.node.id}
                  isCollapsed={collapsedIds.has(layoutNode.node.id)}
                  hasChildren={layoutNode.node.children.length > 0}
                  onClick={handleNodeClick}
                  onToggleCollapse={onToggleCollapse}
                />
              </foreignObject>
            )
          })}
        </g>
      </svg>
    </div>
  )
}
